import { PROMO_LIMITS } from 'constants/tpo';
import type { ParsedPromoOptions } from '../../../config/parsePromoConfig';

const PROMO_TYPE_SHORT_NAMES: Record<string, string> = {
    edlp: 'EDLP',
    merchandising: 'MERCH',
    sm_mdf: 'MDF',
    sm_ism: 'ISM',
    slotting: 'SLOTTING',
    post_audit: 'POST-AUDIT',
    write_off: 'WRITE-OFF',
};

const MANUAL_OFFER_LABELS: Record<string, string> = {
    price_off: 'Discount',
    single: 'Single',
    combination_offer: '',
    buy_for: '',
    buy_save: '',
};

const CURRENCY = '$';

function buildMechanicType(formValues: Record<string, unknown>, parsedOptions: ParsedPromoOptions): string {
    const promoType = formValues['promoType'] as string | undefined;
    const offerType = formValues['offerType'] as string | undefined;
    const manualOffer = formValues['manualOffer'] as string | undefined;
    const discount = formValues['discount'] as number | undefined;
    const discountUnit = formValues['discountUnit'] as string | undefined;
    const buyGet = formValues['buyGet'] as [number?, number?] | undefined;
    const comboUnit = formValues['comboUnit'] as string | undefined;
    const buyFor = formValues['buyFor'] as [number?, number?] | undefined;
    const buySave = formValues['buySave'] as [number?, number?] | undefined;
    const buySaveUnit = formValues['buySaveUnit'] as string | undefined;
    const suggestedOffer = formValues['suggestedOffer'] as string | undefined;

    if (offerType === 'promo_library' && suggestedOffer) {
        return suggestedOffer;
    }

    if (!promoType) return '';

    const promoTypeLabel = parsedOptions.promoTypeOptions.find((o) => o.value === promoType)?.label ?? promoType;

    const offerLabel = offerType === 'manual' ? MANUAL_OFFER_LABELS[manualOffer ?? ''] ?? '' : '';

    let label = `${promoTypeLabel}_${offerLabel}`;

    const isMerchOrMdf = promoType === 'merchandising' || promoType === 'sm_mdf';

    if (isMerchOrMdf) {
        if (manualOffer === 'price_off') {
            if (discountUnit === '%') {
                label += `_${discount ?? ''}${discountUnit}`;
            } else {
                label += `_${CURRENCY}${discount ?? ''}`;
            }
        } else if (manualOffer === 'combination_offer') {
            if (comboUnit === '%') {
                label += `Buy_${buyGet?.[0] ?? 0}_Get_${buyGet?.[1] ?? 0}${comboUnit}`;
            } else if (comboUnit === 'abs') {
                label += `Buy_${buyGet?.[0] ?? 0}_Get_${CURRENCY}${buyGet?.[1] ?? 0}`;
            } else {
                label += `Buy_${buyGet?.[0] ?? 0}_Get_${buyGet?.[1] ?? 0}`;
            }
        } else if (manualOffer === 'buy_for') {
            label += `${buyFor?.[0]}_For_${CURRENCY}${buyFor?.[1]}`;
        } else if (manualOffer === 'buy_save') {
            if (buySaveUnit === 'abs') {
                label += `Buy${CURRENCY}${buySave?.[0] ?? 0}Save${CURRENCY}${buySave?.[1] ?? 0}`;
            } else if (buySaveUnit === '%') {
                label += `Buy${CURRENCY}${buySave?.[0] ?? 0}Save${buySave?.[1] ?? 0}%`;
            }
        }
    } else {
        label += 'single';
    }

    return label.toLocaleUpperCase();
}

export function generatePromoName(
    formValues: Record<string, unknown>,
    parsedOptions: ParsedPromoOptions,
    brandAbbreviation = '',
): string {
    const parts: string[] = [];

    const brand = brandAbbreviation.substring(0, PROMO_LIMITS.BRAND_MAX_LENGTH);
    const extraChars = PROMO_LIMITS.BRAND_MAX_LENGTH - brand.length;
    if (brand) parts.push(brand);

    const promoStartDate = formValues['promoStartDate'];
    if (promoStartDate && typeof promoStartDate === 'object' && 'format' in promoStartDate) {
        parts.push((promoStartDate as { format: (f: string) => string }).format('MM.DD'));
    }

    const promoType = formValues['promoType'] as string | undefined;
    const mechanicType = buildMechanicType(formValues, parsedOptions);
    if (mechanicType && promoType) {
        let shortMechanic = mechanicType;
        if (mechanicType.length > PROMO_LIMITS.MECHANIC_MAX_LENGTH) {
            const mechParts = shortMechanic.split('_');
            mechParts[0] = PROMO_TYPE_SHORT_NAMES[promoType] ?? mechParts[0];
            shortMechanic = mechParts.join('_');
        }
        parts.push(shortMechanic.substring(0, PROMO_LIMITS.MECHANIC_MAX_LENGTH + extraChars));
    }

    return parts.join('_');
}
